import React, { useEffect, useState } from 'react'
import { Modal, Message, Label, Image } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { X } from 'phosphor-react'
import { FlexRowBetween, TitleText } from 'components'
import { SupportTicketService } from 'utility/services/support-ticket'
import { DateUtility } from 'utility'
import { AppPrimaryButton } from 'elements'

const STATUS_MAP = {
    1: { text: 'Open', color: 'blue' },
    2: { text: 'In Progress', color: 'orange' },
    3: { text: 'Resolved', color: 'green' },
    4: { text: 'Closed', color: 'grey' },
}

const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
        return DateUtility.dateToLocalISO(new Date(dateString), 'MM/dd/yyyy')
    } catch {
        return dateString
    }
}

const STATUS_OPTIONS = [
    { key: '1', text: 'Open', value: 1 },
    { key: '2', text: 'In Progress', value: 2 },
    { key: '3', text: 'Resolved', value: 3 },
    { key: '4', text: 'Closed', value: 4 },
]

export const SupportTicketDetailModal = ({ open, onClose, ticketId, onStatusUpdate }) => {
    const [ticket, setTicket] = useState(null)
    const [attachments, setAttachments] = useState([])
    const [loading, setLoading] = useState(false)
    const [updatingStatus, setUpdatingStatus] = useState(false)
    const [error, setError] = useState('')
    const [selectedStatus, setSelectedStatus] = useState(null)
    const [issueTypes, setIssueTypes] = useState([])

    useEffect(() => {
        if (open && ticketId) {
            fetchTicketDetails()
        } else {
            setTicket(null)
            setAttachments([])
            setError('')
        }
    }, [open, ticketId])

    const fetchTicketDetails = async () => {
        try {
            setLoading(true)
            setError('')

            // Fetch issue types first
            try {
                const types = await SupportTicketService.getIssueTypes()
                setIssueTypes(Array.isArray(types) ? types : [])
            } catch (issueTypeError) {
                console.error('Error fetching issue types:', issueTypeError)
                setIssueTypes([])
            }

            // Fetch ticket details
            const ticketData = await SupportTicketService.getById(ticketId)
            setTicket(ticketData)
            setSelectedStatus(ticketData.StatusId)

            // Fetch attachments
            try {
                const attachmentsData = await SupportTicketService.getAttachments(ticketId)
                setAttachments(Array.isArray(attachmentsData) ? attachmentsData : [])
            } catch (attachError) {
                console.error('Error fetching attachments:', attachError)
                setAttachments([])
            }
        } catch (e) {
            setError('Failed to load ticket details')
            console.error('Error fetching ticket:', e)
        } finally {
            setLoading(false)
        }
    }

    const getStatusLabel = (statusId) => {
        const status = STATUS_MAP[statusId] || STATUS_MAP[1]
        return (
            <Label color={status.color} size="large">
                {status.text}
            </Label>
        )
    }

    const getIssueTypeName = (issueTypeId) => {
        if (!issueTypeId || !issueTypes.length) return 'N/A'
        const issueType = issueTypes.find(type => type.IssueTypeId === issueTypeId)
        return issueType?.IssueTypeName || 'N/A'
    }

    const isImageFile = (url) => {
        if (!url) return false
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']
        const lowerUrl = url.toLowerCase()
        return imageExtensions.some(ext => lowerUrl.includes(ext))
    }

    const handleStatusChange = async (e, { value }) => {
        if (value === ticket.StatusId) {
            return // No change
        }

        try {
            setUpdatingStatus(true)
            setError('')
            await SupportTicketService.updateStatus(ticketId, value)
            setSelectedStatus(value)
            setTicket({ ...ticket, StatusId: value })
            if (onStatusUpdate) {
                onStatusUpdate()
            }
        } catch (e) {
            setError('Failed to update ticket status')
            console.error('Error updating status:', e)
        } finally {
            setUpdatingStatus(false)
        }
    }

    return (
        <Modal open={open} onClose={onClose} className="model" size="large">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>
                        {ticket?.TicketNumber || `Ticket #${ticketId}`}
                    </TitleText>
                    <X className="pointer" size={32} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                {loading && (
                    <Message info>
                        <Message.Header>Loading...</Message.Header>
                        <p>Please wait while we fetch ticket details.</p>
                    </Message>
                )}

                {error && (
                    <Message negative>
                        <Message.Header>Error</Message.Header>
                        <p>{error}</p>
                    </Message>
                )}

                {ticket && !loading && (
                    <div>
                        <div style={{ marginBottom: '20px' }}>
                            <FlexRowBetween>
                                <div>
                                    <h3>Ticket Details</h3>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <label style={{ fontSize: '0.875rem', color: '#666', fontWeight: '500' }}>
                                            Update Status
                                        </label>
                                        <Dropdown
                                            placeholder="Change Status"
                                            selection
                                            options={STATUS_OPTIONS}
                                            value={selectedStatus}
                                            onChange={handleStatusChange}
                                            disabled={updatingStatus}
                                            loading={updatingStatus}
                                        />
                                    </div>
                                    {getStatusLabel(ticket.StatusId)}
                                </div>
                            </FlexRowBetween>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <strong>Ticket Number:</strong> {ticket.TicketNumber || `#${ticket.TicketId}`}
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <strong>Requester Name:</strong> {ticket.RequesterName || 'N/A'}
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <strong>Email:</strong> {ticket.RequesterEmail || 'N/A'}
                        </div>

                        {ticket.PhoneNumber && (
                            <div style={{ marginBottom: '15px' }}>
                                <strong>Phone Number:</strong> {ticket.PhoneNumber}
                            </div>
                        )}

                        <div style={{ marginBottom: '15px' }}>
                            <strong>Issue Type:</strong> {getIssueTypeName(ticket.IssueTypeId)}
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <strong>Created Date:</strong> {formatDate(ticket.CreatedOn)}
                        </div>

                        {ticket.UpdatedDate && (
                            <div style={{ marginBottom: '15px' }}>
                                <strong>Last Updated:</strong> {formatDate(ticket.UpdatedDate)}
                            </div>
                        )}

                        <div style={{ marginBottom: '15px' }}>
                            <strong>Message:</strong>
                            <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                {ticket.Message || 'N/A'}
                            </div>
                        </div>

                        {attachments && attachments.length > 0 && (
                            <div style={{ marginBottom: '15px' }}>
                                <strong>Attachments ({attachments.length}):</strong>
                                <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                                    {attachments.map((attachment, index) => {
                                        const fileUrl = attachment.FilePath || attachment.FileURL || attachment.url
                                        return (
                                            <div key={index} style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
                                                {isImageFile(fileUrl) ? (
                                                    <Image
                                                        src={fileUrl}
                                                        alt={`Attachment ${index + 1}`}
                                                        style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px' }}
                                                    />
                                                ) : (
                                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                                        <i className="file icon" style={{ fontSize: '48px', color: '#999' }} />
                                                    </div>
                                                )}
                                                <div style={{ marginTop: '8px', textAlign: 'center' }}>
                                                    <AppPrimaryButton
                                                        onClick={() => window.open(fileUrl, '_blank')}
                                                        type="button"
                                                    >
                                                        View File
                                                    </AppPrimaryButton>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions>
                <AppPrimaryButton onClick={onClose} type="button">
                    Close
                </AppPrimaryButton>
            </Modal.Actions>
        </Modal>
    )
}
